import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Camera, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveCaptureFrames, type CaptureFrameImage } from '@/lib/capture-frames-source';

/**
 * The photographs on a delivered capture, on the screen that asks the
 * operator to confirm what was read off them.
 *
 * CA-05 showed field values alone, so the operator confirming a model
 * number had no way to tell which item they were confirming, and a capture
 * carrying three frames showed none of them. The frames render from the
 * device where the queue record still holds the bytes, and from
 * `GET /capture/{id}/assets` otherwise (src/lib/capture-frames-source.ts).
 *
 * A frame opens full screen inside the app rather than in a new tab. This
 * client installs to the home screen, so a new tab throws the operator out
 * of the app and into the browser, and a device frame is an object URL this
 * component revokes when the screen closes, which would leave that tab
 * holding a blank image it cannot reload.
 *
 * A read URL expires. The screen polls for about two minutes and the URL
 * outlives that comfortably, but a screen left open on a bench does not, so
 * a frame that fails to load asks for fresh URLs once rather than leaving a
 * broken image on a confirmation screen. Every frame is signed in the same
 * request, so they expire together and the thumbnails ask for the refresh
 * as readily as the frame on show.
 *
 * On the shared review route (ADR-006) each frame also carries a Retake, and
 * a "+" adds a frame while the capture holds fewer than three. Both hand a
 * photograph to the caller, which uploads it and calls
 * `POST /capture/{id}/frames`. The picker opens the rear camera on a phone
 * and a file chooser on a desktop, which is where the reviewer is.
 */

interface CaptureFramesProps {
  captureId: string;
  /** From the capture record, in capture order. */
  assetKeys: string[];
  /** A replacement photograph for the frame at `index`. Omit to hide Retake. */
  onRetake?: (index: number, file: File) => void;
  /** A new photograph appended to the capture. Omit to hide the "+". */
  onAddFrame?: (file: File) => void;
  /** Frames the capture may hold, `MAX_ASSETS_PER_CAPTURE` on the API. */
  maxFrames?: number;
  /** Disables Retake and "+" while a change is being sent. */
  busy?: boolean;
  /**
   * Skip the device copy and read the frames from the server. Set once a
   * frame has been retaken: the queue record still holds the old bytes
   * under the same frame count, and would show the photograph that was
   * replaced.
   */
  preferServer?: boolean;
}

export function CaptureFrames({
  captureId,
  assetKeys,
  onRetake,
  onAddFrame,
  maxFrames = 3,
  busy = false,
  preferServer = false,
}: CaptureFramesProps) {
  // The record is re-read every 2.5s while extraction runs, so the array
  // arrives with a new identity each poll while naming the same frames.
  // Joining it gives the effect a dependency that changes when the frames
  // do, rather than one that changes on the clock.
  const keyList = assetKeys.join('\n');
  const [frames, setFrames] = useState<CaptureFrameImage[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [selected, setSelected] = useState(0);
  const [refreshes, setRefreshes] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const pickerRef = useRef<HTMLInputElement | null>(null);
  const pickTargetRef = useRef<{ action: 'retake'; index: number } | { action: 'add' } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const made: string[] = [];
    setStatus('loading');

    resolveCaptureFrames(captureId, keyList ? keyList.split('\n') : [], {
      createObjectUrl: (blob) => {
        const url = URL.createObjectURL(blob);
        made.push(url);
        return url;
      },
      ...(preferServer ? { findQueued: () => Promise.resolve(undefined) } : {}),
    })
      .then((resolved) => {
        if (cancelled) return;
        setFrames(resolved);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      for (const url of made) URL.revokeObjectURL(url);
    };
  }, [captureId, keyList, refreshes, preferServer]);

  // One refresh, whichever frame reports the failure first.
  const refreshOnce = useCallback(
    (frame: CaptureFrameImage) => {
      if (frame.source === 'server' && refreshes === 0) setRefreshes(1);
    },
    [refreshes],
  );

  const close = useCallback(() => setZoomed(false), []);
  useEffect(() => {
    if (!zoomed) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed, close]);

  function pick(target: { action: 'retake'; index: number } | { action: 'add' }) {
    pickTargetRef.current = target;
    pickerRef.current?.click();
  }

  function picked(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    const target = pickTargetRef.current;
    // Cleared so choosing the same photograph twice still fires a change.
    event.target.value = '';
    pickTargetRef.current = null;
    if (!file || !target) return;
    if (target.action === 'retake') onRetake?.(target.index, file);
    else onAddFrame?.(file);
  }

  const canAdd = Boolean(onAddFrame) && assetKeys.length < maxFrames;

  /* One hidden picker serves every Retake and the "+". `capture` asks a
     phone for the rear camera; a desktop browser ignores it and opens a
     file chooser. */
  const picker =
    onRetake || onAddFrame ? (
      <input
        ref={pickerRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={picked}
      />
    ) : null;

  const addButton = canAdd ? (
    <button
      type="button"
      onClick={() => pick({ action: 'add' })}
      disabled={busy}
      aria-label="Add a frame"
      className="flex size-14 items-center justify-center rounded-control border border-dashed border-border-strong text-muted hover:text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
    >
      <Plus className="size-5" aria-hidden="true" />
    </button>
  ) : null;

  if (assetKeys.length === 0 && status !== 'error') {
    return (
      <div className="flex flex-col gap-2 sm:max-w-md">
        <p className="text-sm text-muted">
          This capture carries no photograph. Voice and manual captures record none.
        </p>
        {addButton}
        {picker}
      </div>
    );
  }

  const active = Math.min(selected, Math.max(frames.length - 1, 0));
  const current = frames[active];
  const frameName = frames.length > 1 ? `Frame ${active + 1} of this capture` : 'The photograph on this capture';

  return (
    /* The frames sit beside each other rather than stacked, because the
       values the operator is confirming have to stay on the same screen as
       the photograph they were read off. */
    <div className="flex items-start gap-3">
      {picker}
      {/* The frame box keeps the 3:4 shape of the viewfinder, so what the
          operator confirms against looks like what they shot. */}
      <div className="flex w-full max-w-[11rem] shrink-0 flex-col gap-2 sm:max-w-[13rem]">
        <div className="aspect-[3/4] w-full overflow-hidden rounded-card border border-border bg-surface">
          {current ? (
            <button
              type="button"
              onClick={() => setZoomed(true)}
              aria-label={`Open ${frames.length > 1 ? `frame ${active + 1}` : 'the photograph'} full screen`}
              className="block size-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <img
                src={current.url}
                alt={frameName}
                className="size-full object-contain"
                onError={() => refreshOnce(current)}
              />
            </button>
          ) : (
            <p className="flex size-full items-center justify-center px-4 text-center text-sm text-muted">
              {status === 'error'
                ? 'Could not load the photographs on this capture.'
                : 'Loading the photograph…'}
            </p>
          )}
        </div>
        {onRetake && current && (
          <button
            type="button"
            onClick={() => pick({ action: 'retake', index: active })}
            disabled={busy}
            className="flex min-h-11 items-center justify-center gap-2 rounded-control border border-border text-sm text-surface-foreground hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
          >
            <Camera className="size-4" aria-hidden="true" />
            {frames.length > 1 ? `Retake frame ${active + 1}` : 'Retake'}
          </button>
        )}
      </div>

      {(frames.length > 1 || addButton) && (
        <div className="flex min-w-0 flex-col gap-2">
          <ul className="flex flex-col gap-2">
            {frames.length > 1 && frames.map((frame, index) => (
              <li key={frame.assetKey}>
                <button
                  type="button"
                  aria-label={`Show frame ${index + 1}`}
                  aria-pressed={index === active}
                  onClick={() => setSelected(index)}
                  className={cn(
                    'block overflow-hidden rounded-control ring-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    index === active ? 'ring-accent' : 'ring-border',
                  )}
                >
                  <img
                    src={frame.url}
                    alt=""
                    className="size-14 object-cover"
                    onError={() => refreshOnce(frame)}
                  />
                </button>
              </li>
            ))}
            {addButton && <li>{addButton}</li>}
          </ul>
          {frames.length > 1 && (
            <p className="text-xs text-muted">
              Frame {active + 1} of {frames.length}
            </p>
          )}
        </div>
      )}

      {zoomed && current && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={frameName}
          className="fixed inset-0 z-50 flex flex-col bg-surface"
          onClick={close}
        >
          <div className="flex items-center justify-between gap-2 p-2">
            <p className="pl-2 text-sm text-muted">
              {frames.length > 1 ? `Frame ${active + 1} of ${frames.length}` : 'This capture'}
            </p>
            <button
              type="button"
              onClick={close}
              aria-label="Close the photograph"
              className="flex size-11 items-center justify-center rounded-control text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </div>
          <img
            src={current.url}
            alt={frameName}
            className="min-h-0 flex-1 object-contain pb-4"
            onError={() => refreshOnce(current)}
          />
        </div>
      )}
    </div>
  );
}
